/**
 * The Overview as a one-page markdown status note.
 *
 * What it is for: pasting into an email, or handing to somebody who has no account, on the
 * day it is taken. It is deliberately the same figures the page shows and nothing more —
 * including the rule about who sees the money.
 */

export type SnapshotInput = {
  takenOn: Date
  goLive: string
  daysToGoLive: number
  currentWeek: string
  flag: 'Green' | 'Amber' | 'Red'
  hoursLogged: number
  baselineHours: number
  openDecisions: number
  criticalOpen: number
  /** Roles with nobody confirmed, by name. */
  ownersOutstanding: string[]
  gatesAtRisk: number
  /**
   * Milestone-only figures, or null for a client account.
   *
   * The Burn card on the page is behind `isAdmin` and says "not shown to client accounts"
   * on its face. The export has to honour that, so the caller passes null rather than the
   * snapshot deciding for itself — the page already knows who is looking.
   */
  burn: { billed: number; rate: number; remaining: number } | null
}

const FLAG_LABEL = { Green: 'On track', Amber: 'Watch', Red: 'At risk' } as const

const money = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

export function statusSnapshot(input: SnapshotInput): string {
  const stamp = input.takenOn.toISOString().slice(0, 10)
  const pct = input.baselineHours > 0
    ? ((input.hoursLogged / input.baselineHours) * 100).toFixed(1)
    : '0.0'

  const out: string[] = [
    '# Missional Marketing — status snapshot',
    '',
    `**Taken ${stamp}.** Live figures are in the delivery tracker at`,
    '<https://missional.portal.milestoneconsulting.tech>.',
    '',
    '## Where things stand',
    '',
    `- **Status:** ${FLAG_LABEL[input.flag]}`,
    `- **Go-live:** ${input.goLive} — ${input.daysToGoLive} days away`,
    `- **Current week:** ${input.currentWeek}`,
    `- **Hours:** ${input.hoursLogged.toFixed(1)} of ${input.baselineHours} logged (${pct}%) —`
      + ' a planning estimate on a time-and-materials engagement, not a budget cap',
    `- **Open decisions:** ${input.openDecisions}, of which ${input.criticalOpen} are critical`,
    `- **Gates at risk:** ${input.gatesAtRisk}`,
    '',
    '## Waiting on Missional Marketing',
    '',
  ]

  if (input.ownersOutstanding.length === 0) {
    out.push('Every project role has a confirmed owner.', '')
  } else {
    out.push(
      `${input.ownersOutstanding.length} project `
        + `${input.ownersOutstanding.length === 1 ? 'role has' : 'roles have'} nobody confirmed:`,
      '',
      ...input.ownersOutstanding.map((role) => `- ${role}`),
      '',
    )
  }

  if (input.burn) {
    out.push(
      '## Burn — Milestone only',
      '',
      '_Not for client circulation._',
      '',
      `- **Billed value:** ${money(input.burn.billed)} at ${money(input.burn.rate)}/hr`,
      `- **Remaining against the estimate:** ${input.burn.remaining.toFixed(1)} hrs`,
      '',
    )
  }

  return out.join('\n')
}
